import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useSignupForm, type PackageType } from '../hooks/useSignupForm';
import { apiService } from '@services/api';
import { TermsAcceptance } from './TermsAcceptance';
import { FormValidationAlert } from '@components/FormValidationAlert';
import { SignupEmailCodeModal } from './SignupEmailCodeModal';
import { CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY } from '@/constants';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import { getGestaoPacksFromPricing, getMinPlanCapacity } from '@utils/planPacks';
import { calculateCapacityCost } from '@components/unifiedPricingCalculations';
import { calculateExtraUsersCost, ExtraUserPricing } from '@components/config/generalConfigCalculations';
import { formatCNPJInput, formatPhoneInput, computeDisplayPricing } from './signupFormHelpers';
import AdminAccountFields from './signup/AdminAccountFields';
import CompanyLogoField from './signup/CompanyLogoField';
import CompanyInfoFields from './signup/CompanyInfoFields';
import AddressFields from './signup/AddressFields';
import SignupGestaoPlanSelector from './signup/SignupGestaoPlanSelector';
import SignupNucleusCapacitySelector from './signup/SignupNucleusCapacitySelector';
import SignupExtraUsersSelector from './signup/SignupExtraUsersSelector';
import SignupInvestmentSummary from './signup/SignupInvestmentSummary';

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
export const SignupForm: React.FC = () => {
  const calculatorPrefillAppliedRef = useRef(false);
  const {
    form,
    signupError,
    isSignupLoading,
    isCheckingCNPJ,
    buscandoCEP,
    setValues,
    setSignupError,
    handleBuscarCEP,
    handleSubmit: originalHandleSubmit,
    signupVerificationId,
    setSignupVerificationId,
  } = useSignupForm();

  const [emailCodeModalOpen, setEmailCodeModalOpen] = useState(false);
  const [isVerifyingEmailCode, setIsVerifyingEmailCode] = useState(false);
  const [emailCodeError, setEmailCodeError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formulário antes de enviar
    const isValid = form.validateForm();
    if (!isValid) {
      // Fazer scroll para o topo do formulário
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // O erro será exibido no Alert abaixo
      return;
    }
    
    try {
      setIsVerifyingEmailCode(true);
      setEmailCodeError(null);
      setSignupError('');
      const email = form.values.email.trim().toLowerCase();
      const { data } = await apiService.requestSignupEmailCode(email);
      const id = data?.data?.signupVerificationId as string | undefined;
      if (!data?.success || !id) {
        const msg = data?.message || 'Não foi possível enviar o código de verificação.';
        setSignupError(msg);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setSignupVerificationId(id);
      setEmailCodeModalOpen(true);
    } catch (err: unknown) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSignupError(getFriendlyErrorMessage(err, 'Não foi possível enviar o código de verificação.'));
    } finally {
      setIsVerifyingEmailCode(false);
    }
  };

  const { values: signupData, errors: fieldErrors, handleChange } = form;

  const [showSignupPassword, setShowSignupPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [avatarEditorOpen, setAvatarEditorOpen] = React.useState(false);
  const [pricingConfig, setPricingConfig] = useState<{
    basePrice?: number;
    includedUsers?: number;
    extraUserPricing?: ExtraUserPricing;
    availablePackages?: Array<{
      key: string;
      name?: string;
      description?: string;
      enabled?: boolean;
      icon?: string;
      basePrice?: number;
      includedUsers?: number;
      extraUserPricing?: ExtraUserPricing;
      clientCapacityPacks?: Array<{ capacity: number; price: number }>;
      nucleusCapacityPacks?: Array<{ capacity: number; price: number }>;
      condoCapacityPacks?: Array<{ capacity: number; price: number }>;
    }>;
    packages?: {
      gestao?: { clientCapacityPacks?: Array<{ capacity: number; price: number }>; condoCapacityPacks?: Array<{ capacity: number; price: number }> };
      operador?: { clientCapacityPacks?: Array<{ capacity: number; price: number }>; condoCapacityPacks?: Array<{ capacity: number; price: number }> };
    };
  } | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [customUserCount, setCustomUserCount] = useState<number>(0);
  const [useCustomCount, setUseCustomCount] = useState(false);
  const [acceptedTermIds, setAcceptedTermIds] = useState<string[]>([]);
  /** Todos os termos obrigatórios foram aceitos (habilita o botão Criar conta) */
  const [allTermsAccepted, setAllTermsAccepted] = useState(false);
  const [customNucleusCapacityInput, setCustomNucleusCapacityInput] = useState<string>('');

  useEffect(() => {
    const loadPricingConfig = async () => {
      setLoadingPricing(true);
      try {
        const response = await apiService.getPricingConfig();
        if (response.data.success) {
          setPricingConfig(response.data.data);
        }
      } catch (error) {
        // Silently fail
      } finally {
        setLoadingPricing(false);
      }
    };
    loadPricingConfig();
  }, []);

  const availablePackages = React.useMemo(
    () => (pricingConfig?.availablePackages ?? []).filter((p) => p?.enabled !== false),
    [pricingConfig?.availablePackages]
  );

  const gestaoPlanPacks = React.useMemo(
    () => getGestaoPacksFromPricing(pricingConfig),
    [pricingConfig]
  );

  useEffect(() => {
    if (gestaoPlanPacks.length === 0) return;
    setValues((prev) => {
      if (prev.packageType === 'gestao' && (prev.contractedClientCapacity || 0) > 0) return prev;
      return {
        ...prev,
        packageType: 'gestao',
        contractedClientCapacity: prev.contractedClientCapacity || gestaoPlanPacks[0]?.capacity || getMinPlanCapacity(gestaoPlanPacks),
      };
    });
  }, [gestaoPlanPacks, setValues]);

  useEffect(() => {
    if (calculatorPrefillAppliedRef.current || loadingPricing || !pricingConfig) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const o = parsed as Record<string, unknown>;
    if (o.version !== 1 || typeof o.packageType !== 'string') return;
    const pt = o.packageType as PackageType;
    if (pt !== 'condominio' && pt !== 'gestao' && pt !== 'institucional') return;

    if (pt === 'gestao') {
      const cap = typeof o.clientCapacity === 'number' && o.clientCapacity > 0 ? Math.floor(o.clientCapacity) : 0;
      if (cap <= 0) return;
    }
    if (pt === 'institucional') {
      const nucleus =
        typeof o.nucleusCapacity === 'number' && o.nucleusCapacity > 0 ? Math.floor(o.nucleusCapacity) : 0;
      if (nucleus <= 0) return;
    }

    const findPkg = (t: PackageType) =>
      availablePackages.find((p) => {
        const k = (p.key || '').toLowerCase();
        if (t === 'gestao') return k === 'gestao' || k === 'operador';
        if (t === 'institucional') return k === 'institucional';
        return k === 'condominio';
      });

    queueMicrotask(() => {
      if (calculatorPrefillAppliedRef.current) return;
      calculatorPrefillAppliedRef.current = true;
      try {
        sessionStorage.removeItem(CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY);
      } catch {
        // ignore
      }

      if (pt === 'condominio') {
        setCustomNucleusCapacityInput('');
        setUseCustomCount(false);
        setCustomUserCount(0);
        setValues((prev) => ({
          ...prev,
          packageType: 'condominio',
          contractedClientCapacity: 0,
          contractedNucleusCapacity: 0,
          extraUsersCount: 0,
        }));
        return;
      }

      if (pt === 'gestao') {
        const cap = Math.floor(Number(o.clientCapacity));
        setCustomNucleusCapacityInput('');
        setUseCustomCount(false);
        setCustomUserCount(0);
        setValues((prev) => ({
          ...prev,
          packageType: 'gestao',
          contractedClientCapacity: cap,
          contractedNucleusCapacity: 0,
          extraUsersCount: 0,
        }));
        return;
      }

      const nucleus = Math.floor(Number(o.nucleusCapacity));
      const extras =
        typeof o.extraUsersCount === 'number' && o.extraUsersCount >= 0 ? Math.floor(o.extraUsersCount) : 0;
      const pkg = findPkg('institucional');
      const nucleusPacks = ((pkg as { nucleusCapacityPacks?: { capacity: number }[]; clientCapacityPacks?: { capacity: number }[] })
        ?.nucleusCapacityPacks ??
        (pkg as { clientCapacityPacks?: { capacity: number }[] })?.clientCapacityPacks ??
        []) as Array<{ capacity: number }>;
      const exactN = nucleusPacks.some((p) => p.capacity === nucleus);
      setCustomNucleusCapacityInput(exactN ? '' : String(nucleus));

      const extraPkgList = ((pkg as { extraUserPricing?: { packages?: { quantity: number }[] } })?.extraUserPricing
        ?.packages ?? []) as Array<{ quantity: number }>;
      const matchesExtraPack = extras > 0 && extraPkgList.some((p) => p.quantity === extras);
      if (extras === 0) {
        setUseCustomCount(false);
        setCustomUserCount(0);
      } else if (matchesExtraPack) {
        setUseCustomCount(false);
        setCustomUserCount(0);
      } else {
        setUseCustomCount(true);
        setCustomUserCount(extras);
      }

      setValues((prev) => ({
        ...prev,
        packageType: 'institucional',
        contractedClientCapacity: 0,
        contractedNucleusCapacity: nucleus,
        extraUsersCount: extras,
      }));
    });
  }, [loadingPricing, pricingConfig, availablePackages, setValues]);

  // Config efetiva para exibição: pacote escolhido via availablePackages ou packages (legado) ou config global
  const displayPricing = React.useMemo(
    () =>
      computeDisplayPricing(
        pricingConfig,
        signupData.packageType as 'condominio' | 'gestao' | 'institucional',
        availablePackages,
      ),
    [pricingConfig, signupData.packageType, availablePackages]
  );

  // Ao trocar de plano: zerar/definir default de capacidade
  useEffect(() => {
    if (signupData.packageType === 'condominio') {
      setValues((prev) => ({ ...prev, contractedClientCapacity: 0, contractedNucleusCapacity: 0 }));
    } else if (signupData.packageType === 'gestao' && (signupData.contractedClientCapacity || 0) === 0) {
      const packs =
        displayPricing?.clientCapacityPacks ??
        availablePackages.find((p) => ((p.key || '').toLowerCase() === 'gestao' || (p.key || '').toLowerCase() === 'operador'))?.clientCapacityPacks ??
        [];
      const smallest = packs.length > 0 ? Math.min(...packs.map((pack: { capacity: number; price: number }) => pack.capacity)) : 10;
      setValues((prev) => ({ ...prev, contractedClientCapacity: smallest, contractedNucleusCapacity: 0 }));
    } else if (signupData.packageType === 'institucional' && (signupData.contractedNucleusCapacity || 0) === 0) {
      const packs =
        displayPricing?.nucleusCapacityPacks ??
        availablePackages.find((p) => (p.key || '').toLowerCase() === 'institucional')?.nucleusCapacityPacks ??
        [];
      const smallest = packs.length > 0 ? Math.min(...packs.map((pack: { capacity: number; price: number }) => pack.capacity)) : 3;
      setValues((prev) => ({ ...prev, contractedNucleusCapacity: smallest, contractedClientCapacity: 0 }));
    }
  }, [signupData.packageType, signupData.contractedClientCapacity, signupData.contractedNucleusCapacity, displayPricing?.clientCapacityPacks, displayPricing?.nucleusCapacityPacks, availablePackages, setValues]);

  /** Para pacote gestão: custo de capacidade de clientes (regra: maior pacote abaixo) */
  const calculateClientCapacityCost = (count: number): number =>
    calculateCapacityCost(count, displayPricing?.clientCapacityPacks);

  /** Para pacote institucional: custo de capacidade de núcleos (mesma regra) */
  const calculateNucleusCapacityCost = (count: number): number =>
    calculateCapacityCost(count, displayPricing?.nucleusCapacityPacks ?? displayPricing?.clientCapacityPacks);

  // Função para calcular o custo dos usuários extras customizados
  const calculateCustomExtraUsersCost = (extraUsers: number): number => {
    const config = displayPricing ?? pricingConfig;
    return calculateExtraUsersCost(extraUsers, config?.extraUserPricing as ExtraUserPricing | undefined);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange('confirmPassword')(e.target.value);
  };

  const handlePasswordBlur = () => {
    // validação já é feita via validateSignup; o blur aqui só força a tocar o campo
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleChange('zipCode')(value);
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 8) {
      handleBuscarCEP(value);
    }
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJInput(e.target.value);
    handleChange('cnpj')(formatted);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value);
    handleChange('phone')(formatted);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <FormValidationAlert
        errors={fieldErrors}
        serverError={signupError}
        scrollOnError={true}
      />
      <Grid container spacing={2}>
        <AdminAccountFields
          values={signupData}
          fieldErrors={fieldErrors}
          handleChange={handleChange}
          showPassword={showSignupPassword}
          onTogglePassword={() => setShowSignupPassword(!showSignupPassword)}
          showConfirmPassword={showConfirmPassword}
          onToggleConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
          onConfirmPasswordChange={handleConfirmPasswordChange}
          onPasswordBlur={handlePasswordBlur}
        />

        {/* Dados da Empresa */}
        <CompanyLogoField
          logo={signupData.logo}
          companyName={signupData.companyName}
          avatarEditorOpen={avatarEditorOpen}
          onOpenEditor={() => setAvatarEditorOpen(true)}
          onCloseEditor={() => setAvatarEditorOpen(false)}
          onSaveLogo={(newLogo) => setValues((prev) => ({ ...prev, logo: newLogo }))}
        />
        <CompanyInfoFields
          values={signupData}
          fieldErrors={fieldErrors}
          handleChange={handleChange}
          isCheckingCNPJ={isCheckingCNPJ}
          onCNPJChange={handleCNPJChange}
          onPhoneChange={handlePhoneChange}
        />

        {/* Endereço */}
        <AddressFields
          values={signupData}
          fieldErrors={fieldErrors}
          handleChange={handleChange}
          buscandoCEP={buscandoCEP}
          onZipChange={handleZipChange}
        />

        {/* VCondo Planos — timeline + tabela comparativa */}
        <SignupGestaoPlanSelector
          gestaoPlanPacks={gestaoPlanPacks}
          selectedCapacity={signupData.contractedClientCapacity || 0}
          fieldError={fieldErrors.contractedClientCapacity}
          onSelectCapacity={(cap) => {
            setValues((prev) => ({
              ...prev,
              packageType: 'gestao',
              contractedClientCapacity: cap,
            }));
          }}
        />

        {/* Quantidade de núcleos - apenas para pacote Institucional */}
        {signupData.packageType === 'institucional' && (
          <SignupNucleusCapacitySelector
            nucleusCapacityPacks={(displayPricing?.nucleusCapacityPacks ?? []) as Array<{ capacity: number; price: number }>}
            contractedNucleusCapacity={signupData.contractedNucleusCapacity || 0}
            fieldError={fieldErrors.contractedNucleusCapacity}
            customNucleusCapacityInput={customNucleusCapacityInput}
            onCustomInputChange={setCustomNucleusCapacityInput}
            onChangeCapacity={handleChange('contractedNucleusCapacity')}
            calculateNucleusCapacityCost={calculateNucleusCapacityCost}
          />
        )}

        {/* Seleção de Usuários (apenas Institucional) */}
        {signupData.packageType === 'institucional' && (
          <SignupExtraUsersSelector
            includedUsers={(displayPricing ?? pricingConfig)?.includedUsers || 3}
            extraUsersCount={signupData.extraUsersCount}
            customUserCount={customUserCount}
            useCustomCount={useCustomCount}
            extraUserPricing={(displayPricing ?? pricingConfig)?.extraUserPricing as ExtraUserPricing | undefined}
            loadingPricing={loadingPricing}
            showCustomPricingFootnote={Boolean(pricingConfig?.extraUserPricing?.packages && pricingConfig.extraUserPricing.packages.length > 0)}
            onSelectIncluded={() => {
              handleChange('extraUsersCount')(0);
              setUseCustomCount(false);
              setCustomUserCount(0);
            }}
            onSelectPackage={(quantity) => {
              handleChange('extraUsersCount')(quantity);
              setUseCustomCount(false);
              setCustomUserCount(0);
            }}
            onCustomFocus={() => setUseCustomCount(true)}
            onCustomCountChange={(rawValue) => {
              const value = parseInt(rawValue, 10);
              if (!isNaN(value) && value >= 0) {
                setCustomUserCount(value);
                handleChange('extraUsersCount')(value);
                setUseCustomCount(true);
              } else if (rawValue === '') {
                setCustomUserCount(0);
                handleChange('extraUsersCount')(0);
              }
            }}
            calculateCustomExtraUsersCost={calculateCustomExtraUsersCost}
          />
        )}

        {/* Resumo de Investimento Inicial */}
        {(displayPricing ?? pricingConfig) && (
          <SignupInvestmentSummary
            basePrice={Number((displayPricing ?? pricingConfig)?.basePrice || 0)}
            packageType={signupData.packageType}
            contractedClientCapacity={signupData.contractedClientCapacity || 0}
            contractedNucleusCapacity={signupData.contractedNucleusCapacity || 0}
            extraUsersCount={signupData.extraUsersCount || 0}
            calculateClientCapacityCost={calculateClientCapacityCost}
            calculateNucleusCapacityCost={calculateNucleusCapacityCost}
            calculateCustomExtraUsersCost={calculateCustomExtraUsersCost}
          />
        )}

        {/* Aceite de Termos */}
        <Grid item xs={12}>
          <TermsAcceptance
            onAcceptanceChange={setAcceptedTermIds}
            onAllAccepted={setAllTermsAccepted}
            required={true}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSignupLoading || isVerifyingEmailCode || !allTermsAccepted}
            startIcon={(isSignupLoading || isVerifyingEmailCode) ? <CircularProgress size={20} /> : <LoginIcon />}
            sx={{
              mt: 3,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
            }}
          >
            {(isSignupLoading || isVerifyingEmailCode) ? 'Enviando verificação...' : 'Criar Conta'}
          </Button>
        </Grid>
      </Grid>

      <SignupEmailCodeModal
        open={emailCodeModalOpen}
        email={form.values.email.trim().toLowerCase()}
        isBusy={isSignupLoading || isVerifyingEmailCode}
        error={emailCodeError}
        onClose={() => {
          setEmailCodeError(null);
          setEmailCodeModalOpen(false);
        }}
        onConfirm={async (code) => {
          if (!signupVerificationId) return;
          try {
            setIsVerifyingEmailCode(true);
            setEmailCodeError(null);
            await apiService.verifySignupEmailCode(signupVerificationId, code);
            setEmailCodeModalOpen(false);
            await originalHandleSubmit(acceptedTermIds, signupVerificationId);
          } catch (err: unknown) {
            setEmailCodeError(getFriendlyErrorMessage(err, 'Código inválido. Verifique e tente novamente.'));
          } finally {
            setIsVerifyingEmailCode(false);
          }
        }}
      />
    </Box>
  );
};


